// App.js (updated version)
import React, { useState, useEffect } from 'react';
import { Clock, Star, CircleCheck, Filter, BookOpen, ArrowRight } from 'lucide-react';
import { IndianRupee } from '../../components/CustomIcons';
import FilterPopup from '../../components/FilterPopup';
import PurchasePopup from '../../components/PurchasePopup';
import Footer from '../../components/Footer';
import { imageUrl as imgUrl, apiUrls } from '../../utils/url';
import { postRequest } from '../../utils/networking';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonToastContainer from '../../components/CommonToastContainer';

// Helper functions remain the same
function getPath(fullPath) {
  if (!fullPath) return null;
  const normalized = fullPath.replace(/\\/g, "/");
  const assetsIndex = normalized.indexOf("assets/");
  if (assetsIndex !== -1) {
    return normalized.substring(assetsIndex + 7);
  }
  const asstesIndex = normalized.indexOf("asstes/");
  if (asstesIndex !== -1) {
    return normalized.substring(asstesIndex + 7);
  }
  return normalized;
}

const getProcessedImageUrl = (imageUrlSource) => {
  const FALLBACK_IMAGE =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200"%3E%3Crect width="300" height="200" fill="%23f3f4f6"/%3E%3Ctext x="50%25" y="50%25" font-size="14" text-anchor="middle" dy=".3em" fill="%239ca3af"%3ENo Image%3C/text%3E%3C/svg%3E';

  if (!imageUrlSource) return FALLBACK_IMAGE;

  if (imageUrlSource.startsWith('http')) {
    return imageUrlSource;
  }

  const extractedPath = getPath(imageUrlSource);
  if (!extractedPath) return FALLBACK_IMAGE;

  const cleanPath = extractedPath.replace(/^\/+/, '');
  return `${imgUrl}/${cleanPath}`;
};

const renderStars = (rating) => {
  const totalStars = 5;
  const filledStars = Math.round(rating || 0);
  const stars = [];

  for (let i = 1; i <= totalStars; i++) {
    if (i <= filledStars) {
      stars.push(<Star key={i} className="w-3 h-3 fill-current text-amber-500" />);
    } else {
      stars.push(<Star key={i} className="w-3 h-3 text-amber-500" />);
    }
  }

  return stars;
};

const formatPrice = (price) => {
  let numericPrice;
  if (typeof price === 'string') {
    numericPrice = parseFloat(price.replace(/[^0-9.-]/g, ''));
  } else if (typeof price === 'number') {
    numericPrice = price;
  } else {
    numericPrice = 0;
  }
  return isNaN(numericPrice) ? '0.00' : numericPrice.toFixed(2);
};

export default function Courses() {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [courses, setCourses] = useState([]);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  // Purchase popup state
  const [purchasePopup, setPurchasePopup] = useState({ show: false, course: null });
  const [isPurchasing, setIsPurchasing] = useState(false);

  // Temporary filter states for the popup
  const [tempMinPrice, setTempMinPrice] = useState('');
  const [tempMaxPrice, setTempMaxPrice] = useState('');
  const [tempSelectedLevel, setTempSelectedLevel] = useState('All');
  const [tempSelectedCategory, setTempSelectedCategory] = useState('All');

  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced'];
  const dynamicCategories = ['All', ...Array.from(new Set(courses.map(c => c.catname).filter(Boolean)))];
  const categories = dynamicCategories.length > 1 ? dynamicCategories : ['All', 'E-Commerce'];

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        console.log("API calling...");

        const token = localStorage.getItem("authToken");
        console.log("Token:", token);

        const data = await postRequest(apiUrls.getcourse, {});
        console.log("API Data:", data);

        if (data && data.status === "S") {
          setCourses(data.data || []);
        } else {
          console.log("API Error or Null Data");
          setCourses([]);
        }

        // Fetch purchased courses if token exists
        if (token) {
          try {
            const purchasedResponse = await postRequest(apiUrls.getmycourseorders);
            if (purchasedResponse.status === "S" || purchasedResponse.status === "s") {
              setPurchasedCourses(purchasedResponse.data || []);
            }
          } catch (pErr) {
            console.error("Error fetching purchased courses:", pErr);
          }
        }
      } catch (err) {
        console.error("Fetch Error:", err);
        setCourses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const getCoursePrice = (course) => {
    if (typeof course.price === 'string') {
      return parseFloat(course.price.replace(/[^0-9.-]/g, '')) || 0;
    } else if (typeof course.price === 'number') {
      return course.price;
    } else if (course.rawPrice) {
      return parseFloat(course.rawPrice) || 0;
    }
    return 0;
  };

  const filteredCourses = courses.filter(course => {
    const matchesLevel = selectedLevel === 'All' ||
      (course.levelname && course.levelname.toString().trim().toLowerCase() === selectedLevel.trim().toLowerCase());

    const matchesCategory = selectedCategory === 'All' ||
      (course.catname && course.catname.toString().trim().toLowerCase() === selectedCategory.trim().toLowerCase());

    let matchesPrice = true;
    if (minPrice !== '' || maxPrice !== '') {
      const coursePrice = getCoursePrice(course);
      const min = minPrice !== '' ? parseFloat(minPrice) : null;
      const max = maxPrice !== '' ? parseFloat(maxPrice) : null;

      if (min !== null && !isNaN(min) && max !== null && !isNaN(max)) {
        matchesPrice = coursePrice >= min && coursePrice <= max;
      } else if (min !== null && !isNaN(min)) {
        matchesPrice = coursePrice >= min;
      } else if (max !== null && !isNaN(max)) {
        matchesPrice = coursePrice <= max;
      }
    }

    return matchesLevel && matchesPrice && matchesCategory;
  });

  const handleApplyFilters = () => {
    setSelectedLevel(tempSelectedLevel);
    setSelectedCategory(tempSelectedCategory);
    setMinPrice(tempMinPrice);
    setMaxPrice(tempMaxPrice);
    setShowFilters(false);
  };

  const handleResetFilters = () => {
    setSelectedLevel('All');
    setSelectedCategory('All');
    setMinPrice('');
    setMaxPrice('');
    setTempSelectedLevel('All');
    setTempSelectedCategory('All');
    setTempMinPrice('');
    setTempMaxPrice('');
    setShowFilters(false);
  };

  const handleOpenFilters = () => {
    setTempSelectedLevel(selectedLevel);
    setTempSelectedCategory(selectedCategory);
    setTempMinPrice(minPrice);
    setTempMaxPrice(maxPrice);
    setShowFilters(true);
  };

  const handleImageError = (courseId) => {
    if (!imageErrors[courseId]) {
      setImageErrors(prev => ({
        ...prev,
        [courseId]: true
      }));
    }
  };

  const handleEnrollClick = (course) => {
    setPurchasePopup({ show: true, course: course });
  };

  const handlePurchaseSubmit = async (phoneNumber, paymentDetails) => {
    console.log('🚀 handlePurchaseSubmit triggered with:', { phoneNumber, paymentDetails });
    const { course } = purchasePopup;
    if (!course) {
      console.error('❌ No course found in purchasePopup state');
      return;
    }

    setIsPurchasing(true);

    try {
      console.log('📡 Calling verifycoursepayment API...');
      const data = await postRequest(apiUrls.verifycoursepayment, {
        courseid: course.courseid || course.id,
        razorpay_order_id: paymentDetails?.razorpay_order_id,
        razorpay_payment_id: paymentDetails?.razorpay_payment_id,
        razorpay_signature: paymentDetails?.razorpay_signature,
      });
console.log('paymentdata',data)
      if (data.status === "S") {
        toast.success('Purchase successful!');
        setPurchasePopup({ show: false, course: null });
        setTimeout(() => {
          navigate('/mycourse');
        }, 1500);
      } else {
        throw new Error(data.message || 'Purchase failed. Please try again.');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      throw err;
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleClosePurchasePopup = () => {
    if (!isPurchasing) {
      setPurchasePopup({ show: false, course: null });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-3">⚠️</div>
          <h3 className="text-lg font-bold text-slate-700 mb-1">Error loading courses</h3>
          <p className="text-slate-500 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 px-5 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:shadow-lg transition text-sm"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent flex flex-col">
      <div className="flex-grow pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex-1">
            <div className="mb-6 sticky top-16 z-40 bg-white py-3">
              <div className="flex items-center justify-between gap-4 flex-wrap min-h-[50px]">
                <h2 className="text-black font-bold text-xl flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  Course List
                </h2>
                <div className="hidden sm:block w-24"></div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleOpenFilters}
                    className="px-4 py-2.5 rounded-xl border border-orange-200 bg-white/80 hover:bg-orange-50 transition flex items-center gap-2 text-sm font-medium text-slate-700"
                  >
                    <Filter className="w-5 h-5" />
                    Filter
                  </button>
                </div>
              </div>
            </div>

            <FilterPopup
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              selectedCategory={tempSelectedCategory}
              setSelectedCategory={setTempSelectedCategory}
              selectedLevel={tempSelectedLevel}
              setSelectedLevel={setTempSelectedLevel}
              minPrice={tempMinPrice}
              setMinPrice={setTempMinPrice}
              maxPrice={tempMaxPrice}
              setMaxPrice={setTempMaxPrice}
              categories={categories}
              levels={levels}
              handleApplyFilters={handleApplyFilters}
              handleResetFilters={handleResetFilters}
            />

            {(selectedLevel !== 'All' || selectedCategory !== 'All' || minPrice || maxPrice) && (
              <div className="pt-2 pb-4">
                <p className="text-xs font-medium text-slate-500 mb-2">Active Filters:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedCategory !== 'All' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium">
                      Category: {selectedCategory}
                      <button
                        onClick={() => setSelectedCategory('All')}
                        className="hover:text-orange-900 ml-1 font-bold cursor-pointer"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {selectedLevel !== 'All' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium">
                      Level: {selectedLevel}
                      <button
                        onClick={() => setSelectedLevel('All')}
                        className="hover:text-orange-900 ml-1 font-bold cursor-pointer"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {(minPrice || maxPrice) && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-md text-xs font-medium">
                      Price: {minPrice && maxPrice ? `₹${minPrice} - ₹${maxPrice}` : minPrice ? `≥ ₹${minPrice}` : `≤ ₹${maxPrice}`}
                      <button
                        onClick={() => { setMinPrice(''); setMaxPrice(''); }}
                        className="hover:text-orange-900 ml-1 font-bold cursor-pointer"
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {courses.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-3">📚</div>
                <h3 className="text-lg font-bold text-slate-700 mb-1">No courses available</h3>
                <p className="text-slate-500 text-sm">Check back later for new courses</p>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-3">🔍</div>
                <h3 className="text-lg font-bold text-slate-700 mb-1">No courses found</h3>
                <p className="text-slate-500 text-sm">Try adjusting your search or filter criteria</p>
                <button
                  onClick={handleResetFilters}
                  className="mt-3 px-5 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:shadow-lg transition text-sm"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredCourses.map((course, index) => {
                  let imageUrl;
                  const courseId = course.courseid || course.id || index;

                  if (imageErrors[courseId]) {
                    imageUrl = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                  } else {
                    imageUrl = getProcessedImageUrl(course.imgurl);
                  }

                  return (
                    <div
                      key={courseId}
                      className="bg-white rounded-xl hover:shadow-md transition-all duration-300 border border-slate-300 flex flex-col overflow-hidden"
                    >
                      <div className="relative w-full h-36 sm:h-40 flex-shrink-0 overflow-hidden rounded-t-xl bg-gray-100">
                        <img
                          src={imageUrl}
                          alt={course.coursename || 'Course image'}
                          className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
                          loading="lazy"
                          onError={() => handleImageError(courseId)}
                        />
                        <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white px-1.5 py-0.5 rounded-md text-[10px] font-medium z-10">
                          {course.levelname || 'Beginner'}
                        </div>
                      </div>

                      <div className="p-3 flex flex-col flex-1">
                        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1 line-clamp-2 hover:text-orange-600 transition">
                          {course.coursename || 'Untitled Course'}
                        </h3>
                        <p className="text-slate-500 text-xs mb-2 line-clamp-2">
                          {course.description || 'No description available'}
                        </p>
                        <div className="flex items-center justify-between text-[11px] text-slate-500 mb-2 pb-2 border-b border-orange-100">
                          <div className="flex items-center gap-1">
                            {renderStars(course.rating || 0)}
                            <span className="font-medium text-amber-600 ml-1">{course.rating || '4.5'}</span>
                          </div>
                          <div className="flex items-center gap-1 font-semibold text-slate-700">
                            <IndianRupee className="w-3 h-3" />
                            <span>{formatPrice(getCoursePrice(course))}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{course.duration || '2 hours'}</span>
                          </div>
                        </div>
                        <div className="space-y-1 mb-3">
                          {course.syllabus && course.syllabus.slice(0, 2).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-1.5 text-[11px] text-slate-500">
                              <CircleCheck className="w-3 h-3 flex-shrink-0" />
                              <span className="flex-1 line-clamp-1">{typeof item === 'string' ? item : item.title}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-auto pt-2">
                          {purchasedCourses.some(pc => String(pc.courseid) === String(courseId)) ? (
                            <button
                              onClick={() => navigate('/coursedetail', { state: { id: course.courseid } })}
                              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold py-2 sm:py-2.5 rounded-xl transition-all active:scale-95 hover:shadow-lg hover:shadow-orange-200 text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 group"
                            >
                              View Course
                              <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                console.log('Enroll clicked for course:', course.coursename);
                                handleEnrollClick(course);
                              }}
                              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 transform hover:scale-[1.02] text-sm text-center shadow-sm hover:shadow-md"
                            >
                              Enroll Now
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>
      </div>
      <Footer />


      {/* Purchase Popup */}
      {purchasePopup.show && (
        <PurchasePopup
          course={purchasePopup.course}
          onClose={handleClosePurchasePopup}
          onSubmit={handlePurchaseSubmit}
          isSubmitting={isPurchasing}
        />
      )}
      <CommonToastContainer />
    </div>
  );
}